/**
 * Upload, replace, or remove an organization's logo, shown on the
 * organization page in edit mode to people who can edit it. Super admins
 * can also pick one found on the organization's website.
 * See docs/frontend/components/organization-logo.md.
 */
import { useRef, useState } from 'react';
import { Alert } from 'flowbite-react';
import { leasApi } from '../../api/leas';
import { getUserErrorMessage } from '../../api/client';
import { LOGO_ACCEPT, logoFileProblem } from '../../utils/organizationLogo';
import type { LogoCandidate, LogoCandidateList } from '../../types/api';
import { LogoCandidateGrid } from './LogoCandidateGrid';
import { OrganizationLogo } from './OrganizationLogo';

interface OrganizationLogoEditorProps {
  leaId: number;
  name: string;
  logoUpdatedAt?: string | null;
  /** Show "Find on website" (super admins; needs a website on file) */
  canSearchWebsite?: boolean;
  website?: string | null;
  /** Called after a successful change, to reload the organization */
  onChanged: () => void | Promise<void>;
}

export const OrganizationLogoEditor: React.FC<OrganizationLogoEditorProps> = ({
  leaId,
  name,
  logoUpdatedAt,
  canSearchWebsite = false,
  website,
  onChanged,
}) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState<LogoCandidateList | null>(null);
  const [savingUrl, setSavingUrl] = useState<string | null>(null);

  const handleSearch = async () => {
    setError(null);
    setSuccessMessage(null);
    setSearching(true);
    try {
      setFound(await leasApi.getLogoCandidates(leaId));
    } catch (err) {
      console.error('[OrganizationLogoEditor] Website search failed', { leaId, err });
      setError(getUserErrorMessage(err, 'The website could not be searched.'));
    } finally {
      setSearching(false);
    }
  };

  const handleUseCandidate = async (candidate: LogoCandidate) => {
    setError(null);
    setSuccessMessage(null);
    setSavingUrl(candidate.url);
    try {
      await leasApi.setLogoFromUrl(leaId, candidate.url);
      await onChanged();
      setFound(null);
      setSuccessMessage('Logo saved from the website.');
    } catch (err) {
      console.error('[OrganizationLogoEditor] Saving website logo failed', { leaId, err });
      setError(getUserErrorMessage(err, 'That image could not be saved.'));
    } finally {
      setSavingUrl(null);
    }
  };

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // choosing the same file again should still trigger a change
    if (!file) return;
    setError(null);
    setSuccessMessage(null);
    const problem = logoFileProblem(file);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      await leasApi.uploadLogo(leaId, file);
      await onChanged();
      setSuccessMessage(logoUpdatedAt ? 'Logo replaced.' : 'Logo added.');
    } catch (err) {
      console.error('[OrganizationLogoEditor] Upload failed', { leaId, err });
      setError(getUserErrorMessage(err, 'The logo could not be uploaded.'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!confirm(`Remove the logo for ${name}? Its initials will show instead.`)) return;
    setError(null);
    setSuccessMessage(null);
    setBusy(true);
    try {
      await leasApi.removeLogo(leaId);
      await onChanged();
      setSuccessMessage('Logo removed.');
    } catch (err) {
      console.error('[OrganizationLogoEditor] Remove failed', { leaId, err });
      setError(getUserErrorMessage(err, 'The logo could not be removed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">Logo</h4>
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)} className="mb-2">
          <span className="text-sm">{error}</span>
        </Alert>
      )}
      {successMessage && (
        <Alert color="success" onDismiss={() => setSuccessMessage(null)} className="mb-2">
          <span className="text-sm">{successMessage}</span>
        </Alert>
      )}
      <div className="flex items-center gap-3">
        <OrganizationLogo leaId={leaId} name={name} logoUpdatedAt={logoUpdatedAt} size="lg" />
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={busy}
              className="rounded px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? 'Saving…' : logoUpdatedAt ? 'Replace logo' : 'Upload logo'}
            </button>
            {canSearchWebsite && website && (
              <button
                type="button"
                onClick={handleSearch}
                disabled={busy || searching || !!savingUrl}
                className="rounded border border-blue-600 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-400 dark:text-blue-300 dark:hover:bg-gray-600"
              >
                {searching ? 'Searching website…' : 'Find on website'}
              </button>
            )}
            {logoUpdatedAt && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={busy}
                className="rounded border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Remove
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            PNG, JPEG, GIF, or WebP, up to 5 MB. Square images with a transparent background look best.
          </p>
        </div>
      </div>
      {found && (
        <div className="mt-3">
          {found.candidates.length > 0 ? (
            <LogoCandidateGrid candidates={found.candidates} onUse={handleUseCandidate} savingUrl={savingUrl} disabled={busy} />
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Nothing usable found on {found.website ?? 'the website'}{found.problem ? ` (${found.problem})` : ''}. Upload a file instead.
            </p>
          )}
        </div>
      )}
      <input
        ref={fileInput}
        type="file"
        accept={LOGO_ACCEPT}
        onChange={handleFile}
        className="hidden"
        aria-label={`Choose a logo image for ${name}`}
      />
    </div>
  );
};
